import React, { useCallback, useEffect, useState } from 'react';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogTitle from '@mui/material/DialogTitle';
import { useTranslation } from 'react-i18next';
import { styled } from '@mui/material';
import TextField from '@mui/material/TextField';
import { TRACKABLE_EVENTS, trackEventBudget } from 'helpers/trackerHelpers';
import { Project } from 'routes/BudgetModule/shared/useGetProjects';
import { useUpdateProject } from '../hooks/useUpdateProject';

type Props = {
    show: boolean;
    project: Project;
    existingProjectsNames: string[];
    onClose: () => void;
};

const StyTextField = styled(TextField, {
    name: 'StyTextField',
})(() => ({
    marginLeft: '1rem',
    marginRight: '1rem',
    maxWidth: '100%',
}));

export const EditProjectModal: React.FC<Props> = ({
    show,
    onClose,
    project,
    existingProjectsNames,
}) => {
    const { t } = useTranslation();
    const initialName = project.name;
    const [newProjectName, setIsNewProjectName] = useState(initialName);
    const [isEmptyProjectName, setIsEmptyProjectName] = useState(false);
    const [isExistingProjectName, setIsExistingProjectName] = useState(false);
    const [invalidNameErrorText, setInvalidNameErrorText] = useState('');
    const { mutate: editProjectMutation } = useUpdateProject();

    const confirmEdit = () => {
        trackEventBudget(
            TRACKABLE_EVENTS.budget.settings.projects.confirmPatch,
        );
        editProjectMutation({
            newName: newProjectName,
            projectId: project.id!,
        });
    };

    /**
     * Based on project name user input, check if the name is valid.
     * If not, set the corresponding error.
     */
    const userInputValidation = useCallback(() => {
        setIsEmptyProjectName(newProjectName === '');
        setIsExistingProjectName(
            existingProjectsNames.includes(newProjectName),
        );
        setInvalidNameErrorText(() => {
            if (isEmptyProjectName)
                return t('adminBudgetSpace.addProjectModal.emptyNameError');
            if (isExistingProjectName)
                return t('adminBudgetSpace.addProjectModal.existingNameError');
            return '';
        });
    }, [
        t,
        existingProjectsNames,
        newProjectName,
        isExistingProjectName,
        isEmptyProjectName,
    ]);

    useEffect(() => {
        userInputValidation();
    }, [userInputValidation]);

    return (
        <Dialog
            sx={{ padding: '1rem' }}
            open={show}
            onClose={onClose}
            fullWidth
        >
            <DialogTitle sx={{ fontSize: '20px', fontWeight: '600' }}>
                {t('adminBudgetSpace.editProjectModal.title')}
            </DialogTitle>

            <DialogTitle sx={{ fontSize: '18px', fontWeight: '400' }}>
                {t('adminBudgetSpace.addProjectModal.projectName')}
            </DialogTitle>
            <StyTextField
                value={newProjectName}
                onChange={(event) => {
                    userInputValidation();
                    setIsNewProjectName(event.target.value);
                }}
                variant="outlined"
                error={isEmptyProjectName || isExistingProjectName}
                helperText={invalidNameErrorText}
            />
            <DialogActions>
                <Button
                    onClick={() => {
                        setIsNewProjectName(initialName);
                        onClose();
                    }}
                >
                    {t('common.close')}
                </Button>
                <Button
                    variant="contained"
                    disabled={isEmptyProjectName || isExistingProjectName}
                    onClick={() => {
                        confirmEdit();
                    }}
                >
                    {t('common.confirm')}
                </Button>
            </DialogActions>
        </Dialog>
    );
};
